import { type JobStatus } from '@/features/jobs/job-status'
import { type DateRange } from '@/infra/DateRangeFilter'
import { type NumberRange } from '@/infra/NumberRangeFilter'
import type { Id } from '@/infra/id'
import { type JobProgressionStepStatus } from '@/features/jobs/taxes/progression/JobProgression'
import { type JobScopeChangeStatus } from '@/features/jobs/job-scope-changes/JobScopeChange'
import { type JobPeriodFilterOption } from '@/features/jobs/list/job-period-filter-option'
import { type WaitingOn } from '@/features/jobs/Job'
import {
  clientOwnedSteps,
  inProgressStatuses,
  proOwnedSteps,
} from '@/features/jobs/list/job-list-view'

/**
 * Query string builder for the Job Search API.
 */
export class JobSearchQueryStringBuilder {
  legends = ''
  workspaces = ''
  statuses = ''
  dateCreated = ''
  dateClosed = ''
  dateClaimed = ''
  dateSubmitted = ''
  providerCutAmount = ''
  progressionSteps = ''
  jobScopeChangeStatus = ''
  teams = ''
  period = ''
  archived = ''
  readyToAdvance = ''
  members = ''
  serviceItems = ''
  documentTypes = ''
  waitingOn = ''
  jobIds = ''

  /**
   * Searches for jobs with any legend id.
   *
   * @param ids
   * @returns
   */
  withLegendIds(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.legends += ids
      .filter(Boolean)
      .map((id) => condition('legend_id', '=', id))
      .join(' OR ')
    return this
  }

  /**
   * Searches for jobs within workspaces.
   *
   * @param ids
   */
  withWorkspaceIds(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.workspaces += ids
      .filter(Boolean)
      .map((id) => condition('workspace_id', '=', id))
      .join(' OR ')
    return this
  }

  /**
   * Searches for jobs with any status.
   *
   * @param statuses
   * @returns
   */
  withStatuses(statuses?: JobStatus[] | null): JobSearchQueryStringBuilder {
    if (!statuses) {
      return this
    }

    this.statuses += statuses
      .filter(Boolean)
      .map((s) => condition('status', '=', jobStatusSearchValue(s)))
      .join(' OR ')
    return this
  }

  /**
   * Searches for jobs with any progression step
   */
  withProgressionSteps(
    progressionSteps?: JobProgressionStepStatus[] | null,
  ): JobSearchQueryStringBuilder {
    if (!progressionSteps) {
      return this
    }

    this.progressionSteps += progressionSteps
      .map((s) =>
        condition('progression_step', '=', progressionStepSearchValue(s)),
      )
      .join(' OR ')

    return this
  }

  /**
   * Searches for jobs with any job scope change request status.
   */
  withJobScopeChangeStatus(
    jobScopeChangeStatus?: JobScopeChangeStatus[] | null,
  ): JobSearchQueryStringBuilder {
    if (!jobScopeChangeStatus) {
      return this
    }

    this.jobScopeChangeStatus += jobScopeChangeStatus
      .map((s) =>
        condition(
          'change_request_status',
          '=',
          toJobScopeChangeStatusQueryValue(s),
        ),
      )
      .join(' OR ')

    return this
  }

  /**
   * Searches for jobs where date created is within date range.
   *
   * @param dateRange
   * @returns
   */
  withinDateCreatedRange(
    dateRange?: DateRange | null,
  ): JobSearchQueryStringBuilder {
    if (!dateRange) {
      return this
    }

    this.dateCreated += this.rangeQueryParts('date_created', dateRange).join(
      ' AND ',
    )
    return this
  }

  /**
   * Searches for jobs where date closed is within date range.
   *
   * @param dateRange
   * @returns
   */
  withinDateClosedRange(
    dateRange?: DateRange | null,
  ): JobSearchQueryStringBuilder {
    if (!dateRange) {
      return this
    }

    this.dateClosed += this.rangeQueryParts('date_completed', dateRange).join(
      ' AND ',
    )
    return this
  }

  /**
   * Searched for jobs where date claimed is within date range.
   *
   * @param dateRange
   * @returns
   */
  withinDateClaimedRange(
    dateRange?: DateRange | null,
  ): JobSearchQueryStringBuilder {
    if (!dateRange) {
      return this
    }

    this.dateClaimed += this.rangeQueryParts('date_claimed', dateRange).join(
      ' AND ',
    )
    return this
  }

  /**
   * Searches for jobs where date submitted is within date range.
   *
   * @param dateRange
   * @returns
   */
  withinDateSubmittedRange(
    dateRange?: DateRange | null,
  ): JobSearchQueryStringBuilder {
    if (!dateRange) {
      return this
    }

    this.dateSubmitted += this.rangeQueryParts(
      'date_submitted',
      dateRange,
    ).join(' AND ')
    return this
  }

  withProviderCutAmountRange(
    providerCutAmountRange?: NumberRange | null,
  ): JobSearchQueryStringBuilder {
    if (!providerCutAmountRange) {
      return this
    }

    this.providerCutAmount += this.rangeQueryParts(
      'provider_cut_amount',
      providerCutAmountRange,
    ).join(' AND ')
    return this
  }

  withTeamIds(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.teams += ids
      .filter(Boolean)
      .map((id) => {
        const clientTeam = condition('client_team_id', '=', id)
        const proTeam = condition('pro_team_id', '=', id)

        return `${clientTeam} OR ${proTeam}`
      })
      .join(' OR ')

    return this
  }

  withMemberIds(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.members += ids
      .filter(Boolean)
      .map((id) => condition('members.user_public_id', ':', id))
      .join(' OR ')

    return this
  }

  withServiceItems(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.serviceItems = ids
      .filter(Boolean)
      .map((id) => condition('job_scope.inventory_item_ids', ':', id))
      .join(' OR ')

    return this
  }

  withDocumentTypeShortIds(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.documentTypes += ids
      .filter(Boolean)
      .map((id) => condition('document_type_short_ids', ':', id))
      .join(' OR ')

    return this
  }

  withPeriod(period: JobPeriodFilterOption): JobSearchQueryStringBuilder {
    const currentYear = new Date().getFullYear()
    const startOfTheYear = new Date(currentYear, 0, 1).toISOString()
    const endOfTheYear = new Date(currentYear, 11, 31, 23, 59, 59).toISOString()

    const claimed = '(status=CLAIMED OR status=IDLE OR status=ON_HOLD)'
    const submittedCurrentYear = `(date_submitted >= "${startOfTheYear}" AND date_submitted <= "${endOfTheYear}")`
    const createdCurrentYear = `((status=UNDER_CONSTRUCTION OR status=INFO_GATHERING OR status=UNCLAIMED OR status=PENDING) AND (date_created >= "${startOfTheYear}" AND date_created <= "${endOfTheYear}"))`

    const closedPreviousYears = `(status=CLOSED AND date_submitted < "${startOfTheYear}")`
    const notInProgressPreviousYears = `((status=UNDER_CONSTRUCTION OR status=INFO_GATHERING OR status=UNCLAIMED OR status=PENDING) AND (date_created < "${startOfTheYear}"))`

    switch (period) {
      case 'Current':
        this.period = `${claimed} OR ${submittedCurrentYear} OR ${createdCurrentYear}`
        return this
      case 'Old':
        this.period = `${closedPreviousYears} OR ${notInProgressPreviousYears}`
        return this
      default:
        return this
    }
  }

  /**
   * Searches for jobs waiting on the pro or the client, by the same rules as
   * `whoseTurn`; change both together.
   *
   * The progression step settles it when it can. Otherwise the `waiting_on`
   * field decides, which only compares who touched the job last (a pro's
   * "got it" message flips it to the client).
   */
  withWaitingOn(waitingOn?: WaitingOn | null): JobSearchQueryStringBuilder {
    if (!waitingOn) {
      return this
    }

    const isPro = waitingOn === 'Provider'
    const ownSteps = isPro ? proOwnedSteps : clientOwnedSteps
    const otherSteps = isPro ? clientOwnedSteps : proOwnedSteps
    const isOwnStep = ownSteps
      .map((s) =>
        condition('progression_step', '=', progressionStepSearchValue(s)),
      )
      .join(' OR ')
    const isNotOtherStep = otherSteps
      .map((s) =>
        condition('progression_step', '!=', progressionStepSearchValue(s)),
      )
      .join(' AND ')
    const inProgress = inProgressStatuses
      .map((s) => condition('status', '=', jobStatusSearchValue(s)))
      .join(' OR ')

    const legacyWaitingOn = condition(
      'waiting_on',
      '=',
      isPro ? 'PRO' : 'CLIENT',
    )
    const turn = `(${inProgress}) AND ((${isOwnStep}) OR (${legacyWaitingOn} AND (${isNotOtherStep})))`

    this.waitingOn = isPro
      ? turn
      : `(${turn}) OR ${condition('status', '=', 'INFO_GATHERING')}`
    return this
  }

  /**
   * Searches for jobs with any id.
   */
  withJobIds(ids?: Id[] | null): JobSearchQueryStringBuilder {
    if (!ids) {
      return this
    }

    this.jobIds += ids
      .filter(Boolean)
      .map((id) => condition('id', '=', id))
      .join(' OR ')
    return this
  }

  /**
   * Searches for jobs based on their archived status.
   */
  withArchivalStatus(archived?: boolean) {
    if (!archived) {
      return this
    }

    this.archived = condition('archived', '=', 'true')
    return this
  }

  /**
   * Searches for pending jobs whose advance date has been reached or was never set.
   */
  withReadyToAdvance(readyToAdvance?: boolean) {
    if (!readyToAdvance) {
      return this
    }

    const status = condition('status', '=', 'PENDING')
    const dateReached = condition(
      'advance_allowed_time',
      '<=',
      new Date().toISOString(),
    )
    this.readyToAdvance = `${status} AND (${dateReached} OR advance_allowed_time = null)`
    return this
  }

  /**
   * Returns range query parts.
   */
  rangeQueryParts(
    fieldString: FieldString,
    range: DateRange | NumberRange,
  ): string[] {
    const queryParts = []

    if ('from' in range) {
      if (range.from) {
        queryParts.push(condition(fieldString, '>=', range.from.toISOString()))
      }
      if (range.to) {
        queryParts.push(condition(fieldString, '<=', range.to.toISOString()))
      }
    } else {
      if (range.min) {
        queryParts.push(condition(fieldString, '>=', range.min))
      }
      if (range.max) {
        queryParts.push(condition(fieldString, '<=', range.max))
      }
    }

    return queryParts
  }

  /**
   * Builds the query string.
   *
   * @returns
   */
  build(): string {
    return [
      this.legends,
      this.workspaces,
      this.statuses,
      this.progressionSteps,
      this.jobScopeChangeStatus,
      this.dateCreated,
      this.dateClosed,
      this.dateClaimed,
      this.dateSubmitted,
      this.providerCutAmount,
      this.teams,
      this.period,
      this.archived,
      this.readyToAdvance,
      this.members,
      this.serviceItems,
      this.documentTypes,
      this.waitingOn,
      this.jobIds,
    ]
      .filter((i) => i)
      .map((i) => `(${i})`)
      .join(' AND ')
  }
}

/**
 * Generates a condition and escapes the passed value.
 *
 * @param field
 * @param operator
 * @param value
 * @returns
 */
function condition(
  field: string,
  operator: ConditionOperator,
  value: string | number | boolean | undefined,
): string {
  // eslint-disable-next-line no-useless-escape
  return `${field}${operator}\"${String(value)}\"`
}

function jobStatusSearchValue(status: JobStatus): string {
  switch (status) {
    case 'UnderConstruction':
      return 'UNDER_CONSTRUCTION'
    case 'InfoGathering':
      return 'INFO_GATHERING'
    case 'Unclaimed':
      return 'UNCLAIMED'
    case 'Claimed':
      return 'CLAIMED'
    case 'Closed':
      return 'CLOSED'
    case 'Idle':
      return 'IDLE'
    case 'OnHold':
      return 'ON_HOLD'
    case 'Pending':
      return 'PENDING'
  }
}

function progressionStepSearchValue(step: JobProgressionStepStatus): string {
  switch (step) {
    case 'Unspecified':
      return 'PROGRESSION_STEP_UNSPECIFIED'
    case 'JobStarted':
      return 'JOB_STARTED'
    case 'OpenItems':
      return 'OPEN_ITEMS'
    case 'DraftInReview':
      return 'DRAFT_IN_REVIEW'
    case 'DraftRejected':
      return 'DRAFT_REJECTED'
    case 'DraftApproved':
      return 'DRAFT_APPROVED'
    case 'AuthorizationRequested':
      return 'AUTHORIZATION_REQUESTED'
    case 'AuthorizationSigned':
      return 'AUTHORIZATION_SIGNED'
    case 'ReturnFiled':
      return 'RETURN_FILED'
    case 'NotStarted':
      return 'NOT_STARTED'
    case 'DeliverableCompleted':
      return 'DELIVERABLE_COMPLETED'
    case 'PreparationCompleted':
      return 'PREPARATION_COMPLETED'
    case 'ReviewCompleted':
      return 'REVIEW_COMPLETED'
    case 'ExtensionNotStarted':
      return 'EXTENSION_NOT_STARTED'
    case 'ExtensionInProgress':
      return 'EXTENSION_IN_PROGRESS'
    case 'ExtensionConfirmed':
      return 'EXTENSION_CONFIRMED'
    case 'ExtensionAccepted':
      return 'EXTENSION_ACCEPTED'
    case 'ExtensionRejected':
      return 'EXTENSION_REJECTED'
    case 'ProgressionWaitingOnClient':
      return 'PROGRESSION_WAITING_ON_CLIENT'
    case 'ReadyToFile':
      return 'READY_TO_FILE'
  }
}

type ConditionOperator = '=' | '!=' | '>' | '<' | '>=' | '<=' | ':'

type FieldString =
  | 'date_created'
  | 'date_submitted'
  | 'date_claimed'
  | 'date_completed'
  | 'provider_cut_amount'

/**
 * List of job statuses that are not closed.
 */
export const isNotJobStatusClosed: JobStatus[] = [
  'UnderConstruction',
  'Claimed',
  'OnHold',
  'Idle',
  'InfoGathering',
  'Unclaimed',
  'Pending',
]

function toJobScopeChangeStatusQueryValue(status: JobScopeChangeStatus) {
  switch (status) {
    case 'PendingFinalization':
      return 'PENDING_FINALIZATION'
    case 'PendingAdminApproval':
      return 'PENDING_ADMIN_APPROVAL'
    case 'PendingClientApproval':
      return 'PENDING_CLIENT_APPROVAL'
    case 'Accepted':
      return 'ACCEPTED'
    case 'Rejected':
      return 'REJECTED'
    case 'Unspecified':
      return 'UNSPECIFIED'
    case 'ApplyFailed':
      return 'APPLY_FAILED'
  }
}
